import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, TextField, MenuItem, Grid, Chip, Alert, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Calculate as CalcIcon
} from '@mui/icons-material';
import { DataTable, LoadingState, ConfirmDialog } from '../shared';
import useNotification from '../../hooks/useNotification';
import { pescaService } from '../../services/pescaService';
import LiquidacionDetalle from './LiquidacionDetalle';
import ImportadorLiquidacion from './ImportadorLiquidacion';

const estadoColor = {
  BORRADOR: 'warning',
  LIQUIDADA: 'success',
  PAGADA: 'info',
};

const formatMoney = (val) => {
  if (val == null) return '-';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
};

const LiquidacionesTab = () => {
  const { empresaActiva: empresa } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();

  const rutEmpresa = empresa?.rut || empresa?.rut_empresa;

  // State
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [naves, setNaves] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [anio, setAnio] = useState(new Date().getFullYear());

  // Detail view
  const [detalle, setDetalle] = useState(null);

  // New liquidation dialog
  const [dialogNueva, setDialogNueva] = useState(false);
  const [nueva, setNueva] = useState({});
  const [creando, setCreando] = useState(false);

  // Import dialog
  const [importadorOpen, setImportadorOpen] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [eliminando, setEliminando] = useState(false);

  // Load data
  const cargarLiquidaciones = useCallback(async () => {
    if (!rutEmpresa) return;
    setCargando(true);
    try {
      const data = await pescaService.cargarLiquidaciones(rutEmpresa, anio);
      setLiquidaciones(data || []);
    } catch (err) {
      showNotification('Error al cargar liquidaciones', 'error');
    } finally {
      setCargando(false);
    }
  }, [rutEmpresa, anio]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarNaves = useCallback(async () => {
    if (!rutEmpresa) return;
    try {
      const data = await pescaService.cargarNaves(rutEmpresa);
      setNaves(data || []);
    } catch (err) { /* ignore */ }
  }, [rutEmpresa]);

  useEffect(() => {
    if (rutEmpresa) {
      cargarLiquidaciones();
      cargarNaves();
    }
  }, [rutEmpresa, cargarLiquidaciones, cargarNaves]);

  // Open detail
  const abrirDetalle = async (row) => {
    try {
      const data = await pescaService.obtenerLiquidacion(row.id);
      setDetalle(data);
    } catch (err) {
      showNotification('Error al cargar detalle', 'error');
    }
  };

  // Create new
  const handleCrear = async () => {
    setCreando(true);
    try {
      const payload = {
        ...nueva,
        rut_empresa: rutEmpresa,
        nave_id: nueva.nave_id ? parseInt(nueva.nave_id) : null,
        numero: nueva.numero ? parseInt(nueva.numero) : 0,
        semana: nueva.semana ? parseInt(nueva.semana) : null,
        porcentaje_tripulacion: nueva.porcentaje_tripulacion ? parseFloat(nueva.porcentaje_tripulacion) : 0.40,
        porcentaje_retencion: nueva.porcentaje_retencion ? parseFloat(nueva.porcentaje_retencion) : 0.1525,
      };
      await pescaService.crearLiquidacion(payload);
      showNotification('Liquidación creada', 'success');
      setDialogNueva(false);
      setNueva({});
      cargarLiquidaciones();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setCreando(false);
    }
  };

  // Delete
  const handleEliminar = async () => {
    if (!deleteConfirm.id) return;
    setEliminando(true);
    try {
      await pescaService.eliminarLiquidacion(deleteConfirm.id);
      showNotification('Liquidación eliminada', 'success');
      setDeleteConfirm({ open: false, id: null });
      cargarLiquidaciones();
    } catch (err) {
      showNotification(`Error: ${err.detail || err.message || err}`, 'error');
    } finally {
      setEliminando(false);
    }
  };

  // Columns
  const columns = [
    { field: 'numero', headerName: 'N°', minWidth: 60 },
    { field: 'semana', headerName: 'Semana', minWidth: 80, renderCell: (val) => val || '-' },
    { field: 'fecha_liquidacion', headerName: 'Fecha', minWidth: 110, renderCell: (val) => val || '-' },
    { field: 'nave_nombre', headerName: 'Nave', minWidth: 150, renderCell: (val, row) => {
      const nave = naves.find(n => n.id === row.nave_id);
      return nave?.nombre || '-';
    }},
    { field: 'ingreso_bruto', headerName: 'Ingreso', minWidth: 120, align: 'right', renderCell: formatMoney },
    { field: 'total_descuentos', headerName: 'Descuentos', minWidth: 120, align: 'right', renderCell: formatMoney },
    { field: 'total_liquidacion', headerName: 'Total', minWidth: 120, align: 'right', renderCell: formatMoney },
    {
      field: 'estado', headerName: 'Estado', minWidth: 110,
      renderCell: (val) => <Chip label={val} color={estadoColor[val] || 'default'} size="small" />,
    },
  ];

  const actions = [
    { tooltip: 'Ver detalle', icon: <ViewIcon fontSize="small" />, onClick: abrirDetalle },
    {
      tooltip: 'Eliminar', icon: <DeleteIcon fontSize="small" color="error" />,
      onClick: (row) => setDeleteConfirm({ open: true, id: row.id }),
      disabled: (row) => row.estado !== 'BORRADOR',
    },
  ];

  // If detail is open, render it
  if (detalle) {
    return (
      <LiquidacionDetalle
        liquidacion={detalle}
        rutEmpresa={rutEmpresa}
        onVolver={() => { setDetalle(null); cargarLiquidaciones(); }}
        onActualizado={(liq) => setDetalle(liq)}
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Toolbar */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            select label="Año" value={anio} size="small" fullWidth
            onChange={(e) => setAnio(parseInt(e.target.value))}
          >
            {[...Array(5)].map((_, i) => {
              const y = new Date().getFullYear() - i;
              return <MenuItem key={y} value={y}>{y}</MenuItem>;
            })}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 9 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setImportadorOpen(true)}>
              Importar Excel
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogNueva(true)}>
              Nueva Liquidación
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Table */}
      <DataTable
        columns={columns}
        data={liquidaciones}
        loading={cargando}
        actions={actions}
        onRowClick={abrirDetalle}
        emptyMessage="No hay liquidaciones para el período seleccionado"
      />

      {/* Create Dialog */}
      <Dialog open={dialogNueva} onClose={() => setDialogNueva(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Liquidación</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Número" type="number" size="small" required sx={{ flex: 1 }}
                value={nueva.numero || ''}
                onChange={(e) => setNueva(p => ({ ...p, numero: e.target.value }))}
              />
              <TextField label="Semana" type="number" size="small" sx={{ flex: 1 }}
                value={nueva.semana || ''}
                onChange={(e) => setNueva(p => ({ ...p, semana: e.target.value }))}
              />
            </Box>
            <TextField label="Nave" select fullWidth size="small" required
              value={nueva.nave_id || ''}
              onChange={(e) => setNueva(p => ({ ...p, nave_id: e.target.value }))}
              SelectProps={{ native: true }}
            >
              <option value="">-- Seleccione nave --</option>
              {naves.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Fecha inicio" type="date" size="small" sx={{ flex: 1 }}
                value={nueva.fecha_inicio || ''}
                onChange={(e) => setNueva(p => ({ ...p, fecha_inicio: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField label="Fecha fin" type="date" size="small" sx={{ flex: 1 }}
                value={nueva.fecha_fin || ''}
                onChange={(e) => setNueva(p => ({ ...p, fecha_fin: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
            <TextField label="Fecha liquidación" type="date" fullWidth size="small"
              value={nueva.fecha_liquidacion || ''}
              onChange={(e) => setNueva(p => ({ ...p, fecha_liquidacion: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="% Tripulación" type="number" size="small" sx={{ flex: 1 }}
                value={nueva.porcentaje_tripulacion ?? 0.40}
                onChange={(e) => setNueva(p => ({ ...p, porcentaje_tripulacion: e.target.value }))}
                inputProps={{ step: 0.05, min: 0, max: 1 }}
                helperText="Ej: 0.40 = 40%"
              />
              <TextField label="% Retención" type="number" size="small" sx={{ flex: 1 }}
                value={nueva.porcentaje_retencion ?? 0.1525}
                onChange={(e) => setNueva(p => ({ ...p, porcentaje_retencion: e.target.value }))}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
                helperText="Ej: 0.1525 = 15.25%"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogNueva(false)}>Cancelar</Button>
          <Button onClick={handleCrear} variant="contained" disabled={creando || !nueva.nave_id}
            startIcon={creando ? <CircularProgress size={16} /> : null}>
            {creando ? 'Creando...' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import */}
      <ImportadorLiquidacion
        open={importadorOpen}
        onClose={() => setImportadorOpen(false)}
        rutEmpresa={rutEmpresa}
        onImportado={() => cargarLiquidaciones()}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={handleEliminar}
        title="Eliminar Liquidación"
        message="Esta acción no se puede deshacer. Solo se pueden eliminar liquidaciones en estado BORRADOR."
        confirmText="Eliminar"
        loading={eliminando}
        severity="error"
      />
    </Box>
  );
};

export default LiquidacionesTab;
